/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { SignupService, UserService } from '@services';
import utils from '@utils/utils';
import appActions from '@redux/app';
import PublicMenu from '../../../components/PublicMenu';

import './PrepaySignup.scss';

// Custom method to check if passwords match in Yup validation
function matchPass(ref, minLength, message = '') {
	return this.test({
		name      : 'matchPass',
		exclusive : false,
		message,
		test(value) {
			// Only evaluate if the password minimum length condition is met
			if (this.resolve(ref).length >= minLength) {
				return value === this.resolve(ref);
			}
			// Otherwise, always return true
			return true;
		},
	});
}

// Add the custom method to be used on string fields in validation
Yup.addMethod(Yup.string, 'matchPass', matchPass);

const SignupSchema = Yup.object().shape({
	FirstName : Yup.string().trim().required().label('First Name'),
	LastName  : Yup.string().trim().required().label('Last Name'),
	Email     : Yup.string().trim().required().email('Invalid email format')
		.label('Email/Username'),
	MobilePhone : Yup.string().trim().required().matches(/^([\d]{3})-([\d]{3})-([\d]{4})$/gi, 'Invalid phone number')
		.label('Phone Number'),
	OrganizationName : Yup.string().trim().required().label('Organization Name'),
	Password         : Yup.string().trim().required().min(6, 'Password must be at least 6 characters long')
		.default('')
		.label('Password'),
	ConfirmPassword : Yup.string().trim().matchPass(Yup.ref('Password'), 6, 'Passwords don\'t match'),
});

class PrepaySignup extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading         : false,
			showPassword    : false,
			showConfirmPass : false,
			signInPath      : '/login',
		};

		this._passwordRef = null;
		this._confirmPassRef = null;
		this._changeFieldRef = null;

		this.initialValues = {
			FirstName        : '',
			LastName         : '',
			Email            : '',
			MobilePhone      : '',
			OrganizationName : '',
			Password         : '',
			ConfirmPassword  : '',
			agreedToTerms    : false,
			emailMeInfo      : true,
		};
	}

	componentDidMount() {
		const {
			match : { params : { estId } },
			history : { location : { state }, push },
		} = this.props;
		// if we are missing a valid invoice flag OR estimate ID, redirect to login
		if ((state && !state.validInvoice) || !state || !estId) push('/login');
		else {
			// prepopulate the email field
			this._changeFieldRef('Email', state.email, false);
			// Update our "Sign In" button's path
			this.setState({ signInPath : `/login?estID=${estId}&user=${state.email}` });
		}
	}

	handleShowPassword = (id) => {
		this.setState((prevState) => ({ [id] : !prevState[id] }), () => {
			switch (id) {
				case 'showPassword':
					this._passwordRef.focus();
					break;
				case 'showConfirmPass':
					this._confirmPassRef.focus();
					break;
				default:
					break;
			}
		});
	}

	formatPhone = (event, setFieldValue) => {
		const { target : { value } } = event;
		if ((/^([\d]{3})-([\d]{3})-([\d]{4})$/gi).test(value)) return;
		let phone = value.replace(/[^\d]/gi, '');
		if (phone.length > 10) {
			phone = phone.substr(0, 10);
		}
		if (phone.length === 10) {
			phone = phone.replace(/(\d{3})(\d{3})(\d{4})/gi, '$1-$2-$3');
		}

		setFieldValue('MobilePhone', phone, false);
	}

	formikValidation = async (values) => {
		const errors = {};
		const { agreedToTerms } = values;

		// We are only concerned with checking for errors related to required fields,
		// so we don't need to include a 'then' block
		await SignupSchema.validate(values).catch((err) => {
			if (err.type === 'required') {
				errors.emptyFields = 'Please fill out all fields in the form.';
			}
		});

		// Boolean validation is done here because grouping it into the Yup schema interferes with the
		// generic error message created above
		if (!agreedToTerms) {
			errors.agreedToTerms = 'You must agree to Terms and Conditions before proceeding';
		}

		return errors;
	}

	handleSubmit = ({
		FirstName,
		LastName,
		Email,
		MobilePhone,
		OrganizationName,
		Password,
		ConfirmPassword,
	}) => {
		const {
			history : { push }, match : { params : { estId } }, login, receiveUser,
		} = this.props;

		this.setState({ loading : true });
		SignupService.prepaySignup({
			FirstName,
			LastName,
			Email,
			MobilePhone,
			CompanyName : OrganizationName,
			Password,
			ConfirmPassword,
		}).then(({ status }) => {
			if (status === 201) {
				// Account creation successful, log new user in
				UserService.login({ email : Email, password : Password })
					.then(({ data }) => {
						login();
						receiveUser(data);

						// move to next route (payment)
						push(`/prepay/payment/${estId}`);
					})
					.catch(() => {
						this.setState({ loading : false });
						utils.notify({
							type    : 'info',
							title   : 'An error occurred while logging in',
							message : 'You will be redirected to the login page to sign in.',
							timeOut : 2500,
						});
						setTimeout(() => {
							clearTimeout();
							push({
								pathname : '/login',
								search   : `?estID=${estId}&user=${encodeURIComponent(Email)}`,
							});
						}, 3000);
					});
			}
		}).catch(({ response : { data } }) => {
			this.setState({ loading : false });
			if (data.find((e) => e.ErrorCode === 22)) {
				utils.notify({
					type    : 'info',
					title   : 'An account with this email exists',
					message : 'You will be redirected to the login page.',
					timeOut : 2500,
				});
				setTimeout(() => {
					clearTimeout();
					push({
						pathname : '/login',
						search   : `?estId=${estId}&user=${encodeURIComponent(Email)}`,
					});
				}, 3000);
			} else if (data.find((e) => e.ErrorCode === 26)) {
				utils.notify({
					type    : 'error',
					title   : 'Another account with this email exists',
					message : 'Please use a different email address.',
					timeOut : 2500,
				});
			} else {
				utils.notify({
					type    : 'error',
					message : 'An error occurred. Please try again.',
					timeOut : 1500,
				});
			}
		});
	}

	// Add this element to the end of the Form when we have some functionality for it
	// <div className="form-group col-xs-12 checkbox-group">
	// 	<Field
	// 		name="emailMeInfo"
	// 		render={({ field }) => (
	// 			<input
	// 				type="checkbox"
	// 				id="emailMeInfo"
	// 				checked={field.value}
	// 				{...field /* eslint-disable-line */}
	// 			/>
	// 		)}
	// 	/>
	// 	<label htmlFor="emailMeInfo" className="control-label checkbox-label">
	// 		Send me more info about RollKall
	// 	</label>
	// </div>

	render() {
		const {
			showPassword,
			showConfirmPass,
			loading,
			signInPath,
		} = this.state;
		return (
			<div id="prepay-signup">
				<PublicMenu signInPath={signInPath} />
				<div id="signup-content" className="container">
					<div id="spacer" className="row hidden-xs" />
					<div className="row">
						<div id="form-container" className="col-xs-10 col-xs-offset-1 col-md-6 col-md-offset-3">
							<Formik
								initialValues={{ ...this.initialValues }}
								validationSchema={SignupSchema}
								validate={this.formikValidation}
								validateOnChange={false}
								validateOnBlur={false}
								onSubmit={this.handleSubmit}
							>
								{({ errors, setFieldValue }) => {
									this._changeFieldRef = setFieldValue;
									return (
										<Form id="signup-form">
											<div id="form-header" className="col-xs-12 text-center">
												<h3>Let&apos;s get you set up!</h3>
												<small className="text-danger">{errors.emptyFields}</small>
											</div>
											<div className="form-group col-xs-12 col-sm-6">
												<label htmlFor="FirstName" className="control-label">First Name</label>
												<Field
													name="FirstName"
													id="FirstName"
													className="form-control"
												/>
											</div>
											<div className="form-group col-xs-12 col-sm-6">
												<label htmlFor="LastName" className="control-label">Last Name</label>
												<Field
													name="LastName"
													id="LastName"
													className="form-control"
												/>
											</div>
											<div className="form-group col-xs-12 col-sm-6">
												<label htmlFor="Email" className="control-label">Email/Username</label>
												<Field
													name="Email"
													type="email"
													id="Email"
													readOnly
													className="form-control"
												/>
												<small className="text-danger">{!errors.emptyFields && errors.Email}</small>
											</div>
											<div className="form-group col-xs-12 col-sm-6">
												<label htmlFor="MobilePhone" className="control-label">Phone Number</label>
												<Field
													name="MobilePhone"
													type="tel"
													placeholder="123-456-7890"
													onBlur={(e) => this.formatPhone(e, setFieldValue)}
													id="MobilePhone"
													className="form-control"
												/>
												<small className="text-danger">{!errors.emptyFields && errors.MobilePhone}</small>
											</div>
											<div className="form-group col-xs-12">
												<label htmlFor="OrganizationName" className="control-label">Business Name</label>
												<Field
													name="OrganizationName"
													id="OrganizationName"
													className="form-control"
												/>
											</div>
											<div className="form-group col-xs-12">
												<label htmlFor="Password" className="control-label">Password</label>
												<Field
													name="Password"
													type={showPassword ? 'text' : 'password'}
													id="Password"
													placeholder="Password must be at least 6 characters long"
													className="form-control input-padded-right"
													innerRef={(ref) => { this._passwordRef = ref; }}
												/>
												<small className="text-danger">{!errors.emptyFields && errors.Password}</small>
												<button
													tabIndex="-1"
													type="button"
													id="showPassword"
													className={`btn btn-link rkf ${showPassword ? 'rkf-eye-hidden' : 'rkf-eye'}`}
													onClick={({ target : { id } }) => this.handleShowPassword(id)}
												/>
											</div>
											<div className="form-group col-xs-12">
												<label htmlFor="ConfirmPassword" className="control-label">Confirm Password</label>
												<Field
													name="ConfirmPassword"
													type={showConfirmPass ? 'text' : 'password'}
													id="ConfirmPassword"
													className="form-control input-padded-right"
													innerRef={(ref) => { this._confirmPassRef = ref; }}
												/>
												<small className="text-danger">{errors.ConfirmPassword}</small>
												<button
													tabIndex="-1"
													type="button"
													id="showConfirmPass"
													className={`btn btn-link rkf ${showConfirmPass ? 'rkf-eye-hidden' : 'rkf-eye'}`}
													onClick={({ target : { id } }) => this.handleShowPassword(id)}
												/>
											</div>
											<div className="form-group col-xs-6 col-xs-offset-3 text-center">
												<button
													id="submit-button"
													type="submit"
													className="btn btn-primary form-control"
												>
													{loading ? 'Loading...' : 'Next'}
												</button>
											</div>
											<div className="form-group col-xs-12 checkbox-group">
												<Field
													name="agreedToTerms"
													render={({ field }) => (
														<input
															type="checkbox"
															id="agreedToTerms"
															checked={field.value}
															{...field /* eslint-disable-line */}
														/>
													)}
												/>
												<label htmlFor="agreedToTerms" className="control-label checkbox-label">
													By signing up, I agree to the&nbsp;
													<a href="https://www.rollkall.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
													.
												</label>
												<br />
												<small className="text-danger">{errors.agreedToTerms}</small>
											</div>
										</Form>
									);
								}}
							</Formik>
						</div>
					</div>
					<div id="spacer" className="row visible-xs" />
				</div>
			</div>
		);
	}
}

const mapDispatchToProps = (dispatch) => ({
	login       : bindActionCreators(appActions, dispatch).login,
	receiveUser : bindActionCreators(appActions, dispatch).receiveUser,
});

PrepaySignup.propTypes = {
	history     : PropTypes.object.isRequired,
	match       : PropTypes.object.isRequired,
	login       : PropTypes.func.isRequired,
	receiveUser : PropTypes.func.isRequired,
};

export default connect(null, mapDispatchToProps)(PrepaySignup);
